import { useOutbox } from '@carrent/core'
import { formatDateTime } from '@carrent/domain'
import { Icon, PageHeader } from '@carrent/ui'

/**
 * Ожидают отправки.
 *
 * Экран нужен не для красоты: когда фотография не уходит третий час,
 * водителю нужно понять почему и что делать — а не гадать, потерялась
 * ли работа. Отсюда ручной повтор и возможность убрать задачу.
 */

const TYPE_LABELS = {
  upload_photo: 'Фотография',
  start_trip: 'Начало поездки',
  finish_trip: 'Завершение поездки',
  create_event: 'Событие поездки',
}

const CATEGORY_LABELS = {
  front: 'спереди',
  back: 'сзади',
  left: 'слева',
  right: 'справа',
  interior: 'салон',
  odometer: 'одометр',
  extra: 'дополнительно',
}

function describe(task) {
  const base = TYPE_LABELS[task.type] || task.type
  const category = task.payload?.category
  return category ? `${base} — ${CATEGORY_LABELS[category] || category}` : base
}

function TaskRow({ task, onRetry, onRemove }) {
  const isFailed = task.status === 'failed'
  const isSending = task.status === 'in_flight'

  return (
    <li className={`queue-item ${isFailed ? 'queue-item--failed' : ''}`}>
      <span className="queue-item__icon">
        {isSending ? (
          <span className="spinner spinner--sm" />
        ) : (
          <Icon name={isFailed ? 'warning' : 'trips'} size={18} />
        )}
      </span>

      <span className="queue-item__body">
        <span className="queue-item__title">{describe(task)}</span>
        <span className="queue-item__meta">
          {formatDateTime(task.clientOccurredAt)}
          {task.attempts > 0 && ` · попыток: ${task.attempts}`}
        </span>
        {isFailed && task.error && (
          <span className="queue-item__error">{task.error}</span>
        )}
      </span>

      {isFailed && (
        <span className="queue-item__actions">
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => onRetry(task.id)}
          >
            Повторить
          </button>
          <button
            type="button"
            className="btn-icon"
            onClick={() => onRemove(task.id)}
            aria-label="Убрать из очереди"
          >
            <Icon name="close" size={16} />
          </button>
        </span>
      )}
    </li>
  )
}

export default function QueuePage() {
  const { tasks, pending, failed, online, loading, retry, remove, retryAll } =
    useOutbox()

  return (
    <div>
      <PageHeader title="Ожидают отправки" backTo="/more" />

      {!online && (
        <div className="p-page">
          <div className="alert alert--info">
            <Icon name="warning" size={14} />
            Сети нет. Всё сохранено на устройстве и уйдёт само, когда связь
            появится.
          </div>
        </div>
      )}

      <div className="p-page flex-col gap-sm">
        {loading && (
          <>
            <span className="skeleton queue-item__skeleton" />
            <span className="skeleton queue-item__skeleton" />
          </>
        )}

        {!loading && tasks.length === 0 && (
          <div className="state">
            <Icon name="check" size={32} />
            <p className="state__title">Всё отправлено</p>
            <p className="state__text">
              Незавершённых задач нет — можно продолжать работу.
            </p>
          </div>
        )}

        {!loading && failed.length > 0 && (
          <div className="queue-section">
            <div className="queue-section__head">
              <h2 className="section-title">Не отправлено</h2>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={retryAll}
                disabled={!online}
              >
                Повторить все
              </button>
            </div>
            <ul className="queue-list">
              {failed.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onRetry={retry}
                  onRemove={remove}
                />
              ))}
            </ul>
          </div>
        )}

        {!loading && pending.length > 0 && (
          <div className="queue-section">
            <h2 className="section-title">В очереди</h2>
            <ul className="queue-list">
              {pending.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onRetry={retry}
                  onRemove={remove}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
